/**
 * Kyc — диспетчер верификации: по data_source рендерит экран сбора (internal/sumsub/idenfy),
 * плюс собственные состояния входа (loading / error / approved).
 */
import { useEffect, useState } from "react";

import { useBackButton, useMainButton } from "@/telegram/useMainButton";
import { FallbackButton, Screen } from "@/components/Screen";
import { friendlyError, kycApi, type KycStatus, type PassportProfileIn } from "@/lib/api";
import { KycManual } from "@/screens/onboarding/kyc/KycManual";
import { KycSumsub } from "@/screens/onboarding/kyc/KycSumsub";
import { KycIdenfy } from "@/screens/onboarding/kyc/KycIdenfy";
import { ApprovedCard, ErrorCard, SkeletonBlock } from "@/screens/onboarding/kyc/shared";

type Props = { onNext: (status: KycStatus) => void; onBack: () => void };

type EntryStage =
  | { kind: "loading" }
  | { kind: "error"; message: string }
  | { kind: "sumsub" }
  | { kind: "idenfy" }
  | { kind: "manual"; rejectionReason?: string | null; cheat?: boolean; profile?: PassportProfileIn | null }
  | { kind: "approved"; latest: KycStatus };

export function Kyc(props: Props) {
  const [stage, setStage] = useState<EntryStage>({ kind: "loading" });

  useEffect(() => {
    (async () => {
      try {
        const status = await kycApi.status();
        if (status.answer === "GREEN") {
          setStage({ kind: "approved", latest: status });
          return;
        }
        // Экран сбора определяет глобальная настройка оператора (ось data_source),
        // а не режим ранее созданной анкеты — иначе при переключении клиент со старой
        // анкетой продолжал бы видеть прежний экран. Фоллбэк на legacy-поле mode.
        const ds = status.process_config?.data_source;
        const internal = ds === "internal" || status.mode === "manual";
        if (internal) {
          setStage({
            kind: "manual",
            rejectionReason: status.answer === "RED" ? status.moderation_comment : null,
            cheat: status.kyc_cheat,
            profile: status.confirmed_profile,
          });
        } else if (ds === "idenfy") {
          setStage({ kind: "idenfy" });
        } else {
          setStage({ kind: "sumsub" });
        }
      } catch (e) {
        setStage({ kind: "error", message: friendlyError(e) });
      }
    })();
  }, []);

  // Делегируем дочерним экранам, которые сами управляют кнопками Telegram —
  // поэтому здесь кнопки НЕ трогаем, иначе родитель перетёр бы «Назад» ребёнка.
  if (stage.kind === "manual")
    return (
      <KycManual {...props} rejectionReason={stage.rejectionReason} cheat={stage.cheat} initialProfile={stage.profile} />
    );
  if (stage.kind === "sumsub") return <KycSumsub {...props} />;
  if (stage.kind === "idenfy") return <KycIdenfy {...props} />;
  return <KycEntryStatus stage={stage} onNext={props.onNext} onBack={props.onBack} />;
}

function KycEntryStatus({
  stage,
  onNext,
  onBack,
}: {
  stage: Extract<EntryStage, { kind: "loading" | "error" | "approved" }>;
  onNext: (status: KycStatus) => void;
  onBack: () => void;
}) {
  useBackButton(onBack);
  useMainButton({
    text: "Продолжить",
    onClick: () => {
      if (stage.kind === "approved") onNext(stage.latest);
    },
    visible: stage.kind === "approved",
    active: stage.kind === "approved",
  });

  if (stage.kind === "loading") {
    return (
      <Screen title="Верификация личности">
        <SkeletonBlock />
      </Screen>
    );
  }
  if (stage.kind === "error") {
    return (
      <Screen title="Верификация личности">
        <ErrorCard message={stage.message} />
      </Screen>
    );
  }
  return (
    <Screen title="Верификация пройдена ✓">
      <ApprovedCard />
      <FallbackButton text="Продолжить" onClick={() => onNext(stage.latest)} />
    </Screen>
  );
}
